/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */
import React, { useCallback, useMemo, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "beautiful-react-hooks";
import { connect, useDispatch } from "react-redux";
import { Trans } from "@lingui/macro";
import get from "lodash/get";
import { JsonLd } from "react-schemaorg";
import LazyHydrate from "react-lazy-hydration";
import { sortBy } from "lodash";
import * as types from "../../stores/types";
import routes from "../../routes";
import { isMobileUserAgent, getUserAgent } from "../../functions/userAgent";
import { Api } from "../../functions/fetchFromApi";
import inclineIfNeeded from "../../functions/inclineIfNeeded";
import detectRoute from "../../functions/route/detectRoute";
import { fetchDefaultCurrency } from "../../functions/currency";
import toQueryString from "../../functions/toQueryString";
import { sendListViewedEvent } from "../../functions/analytics";
import { fetchLinkings, fetchOverview } from "../../functions/fetchData";
import reverseUrl from "../../functions/reverseUrl";
import replaceImgTagsInHtml from "../../functions/optimizeImagesInHtml";
import getCurrentLanguage from "../../functions/languages/getCurrentLanguage";
import useCurrentLanguage from "../../functions/languages/useCurrentLanguage";
import withRedirectToKnownLang from "../../functions/languages/withRedirectToKnownLang";
import Root from "../../components/_Root";
import StackedAccordions from "../../components/StackedAccordions";
import { isSSR } from "../../components/NoSSR";
import ScrollHook from "../../components/ScrollHook";
import { CanonicalAuto } from "../../components/Canonical";
import Hero from "../../components/Hero";
import { getProductLink } from "../../functions/getProductLink";
import getDomainZone from "../../functions/url/getDomainZone";
import { AlternateAuto } from "../../components/Alternate";
import LinkingsGroup from "../../components/Linking";
import { usePageViewTracking } from "../../functions/usePageViewTracking";
import { Categories } from "../../components/Categories";
import getDestinationLinksList from "../../functions/getDestinationLinksList";
import { fetchCityAttractionsWithTickets } from "../../functions/fetchCityAttractionsWithTickets";
import ProductsSection from "./components/ProductsSection";
import SeoSection from "./components/SeoSection";

import "./Attraction.css";

const LazyAudioGuideSection = lazy(() => import("../../components/AudioGuideSection/Index"));
const LazyCityLinkSection = lazy(() => import("../../components/CityLinkSection"));
const LazyEmailSubscriptionSection = lazy(() =>
  import("../../components/EmailSubscriptionSection"),
);
const LazyMostRecommended = lazy(() => import("../../components/MostRecommended"));
const LazyReviewsSection = lazy(() => import("./components/ReviewsSection"));
const LazyLinkingsGroups = lazy(() =>
  import("../../components/Linking").then(module => ({ default: module.LinkingsGroups })),
);
const LazyButtonlikeLinksBlock = lazy(() => import("../../components/ButtonlikeLinksBlock"));
const LazySitemapBlock = lazy(() =>
  import("../../components/SitemapBlock").then(module => ({ default: module.SitemapBlock })),
);

const Attraction = ({
  city = {},
  products = {},
  attraction,
  destinations,
  linkings,
  seoHtml = "",
  mostRecommended,
  overallCount,
  path = "",
  reviews = {},
  attractionId,
  cityAttractionsWithoutTickets,
  recommendedCities,
  productsPerPage,
  currency,
  interests,
  filters,
  userId,
}) => {
  const lang = useCurrentLanguage();
  const DNSZone = useMemo(() => getDomainZone(lang), [lang]);
  const cityId = city.id;
  const currentPath = isSSR ? path : window.location.pathname;
  const { pathname } = useLocation();
  const route = useMemo(
    () =>
      detectRoute(
        routes.filter(r => /attraction-tickets/i.test(r.name)),
        pathname,
      ),
    [pathname],
  );
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isMobileView = !isSSR && isMobile;
  const dispatch = useDispatch();
  usePageViewTracking("attraction", { userId, attractionId });

  const memoizedData = useMemo(
    () => ({
      city: { ...city },
      attraction: { ...attraction },
      cityName: inclineIfNeeded(city.name, "in", lang),
      productListId: `attraction_${attraction?.slug}_${attraction?.id}`,
      citiesLinks: sortBy(
        getDestinationLinksList({
          name: "city",
          destinations: recommendedCities,
          destinationsParams: {
            cityId: "id",
            citySlug: "slug",
          },
          lang,
        }),
        ["text"],
      ),
    }),
    [city, attraction, lang, recommendedCities],
  );

  const { cityName, productListId, citiesLinks } = memoizedData;

  const onProductListShown = useCallback(() => {
    sendListViewedEvent(productListId, products.products, lang);
  }, [productListId, products.products, lang]);

  const findPriceByCurrency = useCallback((prices, currency) => {
    return prices.find(price => price.currencyCode === currency);
  }, []);

  const lowestPrice = useMemo(
    () => findPriceByCurrency(memoizedData.city.minPrice, currency),
    [memoizedData.city.minPrice, currency, findPriceByCurrency],
  );

  const defaultProductsGridProps = useMemo(
    () => ({
      listId: productListId,
      perPage: productsPerPage,
      isMobile: isMobileView,
      lang,
    }),
    [productListId, productsPerPage, isMobileView, lang],
  );

  const jsonLdData = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Product",
      url: `https://wegotrip.${DNSZone}${currentPath}`,
      sku: memoizedData.attraction?.id,
      name: memoizedData.attraction?.name,
      description: memoizedData.attraction?.meta_description,
      brand: { "@type": "Brand", name: "WeGoTrip" },
      image: memoizedData.attraction?.preview,
      ...(reviews.count
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: reviews.averageRating,
              reviewCount: reviews.count,
            },
          }
        : {}),
      ...(reviews.count
        ? {
            review: reviews.reviews.map(review => ({
              "@type": "Review",
              author: { "@type": "Person", name: review.name },
              datePublished: review.date,
              description: review.text,
              reviewRating: {
                "@type": "Rating",
                bestRating: "5",
                ratingValue: review.rating,
                worstRating: "1",
              },
            })),
          }
        : {}),
      ...(products.count
        ? {
            offers: products.products.map(product => ({
              "@type": "Offer",
              price: product.price,
              priceCurrency: product.currencyCode,
              url: `https://wegotrip.${DNSZone}${getProductLink(lang, product)}`,
              availability: "https://schema.org/InStock",
            })),
          }
        : {}),
    }),
    [DNSZone, currentPath, memoizedData.attraction, reviews, products, lang],
  );

  const faqJsonLdData = useMemo(
    () =>
      memoizedData.attraction?.faqs
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: memoizedData.attraction.faqs.map(question => ({
              "@type": "Question",
              name: question.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: question.body,
              },
            })),
          }
        : null,
    [memoizedData.attraction?.faqs],
  );

  if (isSSR) return null;

  return (
    <Root stickyHeader searchInHeader destinations={destinations}>
      {route && <AlternateAuto route={route} />}
      {route && <CanonicalAuto route={route} />}

      <Hero
        city={memoizedData.city}
        lowestPrice={lowestPrice}
        initialMetaTitle={memoizedData.city.meta_title}
        initialMetaDescription={memoizedData.city.meta_description}
        attraction={memoizedData.attraction}
        current="attraction"
        theme="no-background"
      />

      <div className="Wrapper attraction-page-wrapper">
        <LazyHydrate whenVisible>
          <ScrollHook once="shown" showOn=".City__products" onChanged={onProductListShown} />
          <Suspense fallback={null}>
            <LazyAudioGuideSection />
          </Suspense>
          <ProductsSection
            isMobile={isMobile}
            dispatch={dispatch}
            attraction={attraction}
            lang={lang}
            defaultProductsGridProps={defaultProductsGridProps}
            products={products}
            cityId={cityId}
            currency={currency}
            attractionId={attractionId}
            filters={filters}
          />
        </LazyHydrate>

        <LazyHydrate whenVisible>
          {memoizedData.attraction?.nearbyAttractions?.length > 0 && (
            <Categories
              products={memoizedData.attraction.nearbyAttractions}
              lang={lang}
              city={memoizedData.city}
              type="attraction"
              title={<Trans>Top sights near {memoizedData.attraction?.name}</Trans>}
            />
          )}
        </LazyHydrate>

        <LazyHydrate whenVisible>
          <Suspense fallback={null}>
            <LazyCityLinkSection city={memoizedData.city} lang={lang} />
            <LazyEmailSubscriptionSection city={memoizedData.city} attractionId={attractionId} />
          </Suspense>
        </LazyHydrate>

        <LazyHydrate whenVisible>
          <Suspense fallback={null}>
            <LazyMostRecommended
              categoryTitle={memoizedData.attraction?.name || null}
              city={memoizedData.city}
              mostRecommended={mostRecommended}
              lang={lang}
            />
          </Suspense>
        </LazyHydrate>

        <LazyHydrate whenVisible>
          <Suspense fallback={null}>
            <LazyReviewsSection
              cityId={cityId}
              overallCount={overallCount}
              isMobileView={isMobileView}
              lang={lang}
              attractionId={attractionId}
              attractionName={memoizedData.attraction?.name}
              cityName={cityName}
            />
          </Suspense>
        </LazyHydrate>

        <LazyHydrate whenVisible>
          <Suspense fallback={null}>
            <LazyLinkingsGroups
              interests={interests}
              dispatch={dispatch}
              cityAttractionsWithoutTickets={cityAttractionsWithoutTickets?.results || []}
              fetchCityAttractionsWithTickets={() =>
                fetchCityAttractionsWithTickets({
                  cityId,
                  countryId: memoizedData.city.country?.id,
                  lang,
                })
              }
              city={memoizedData.city}
              cityId={cityId}
              cityName={cityName}
              lang={lang}
              recommendedCities={recommendedCities}
              isMobile={isMobile}
              citiesLinks={citiesLinks}
            />
          </Suspense>
        </LazyHydrate>

        <LazyHydrate whenVisible>
          {!!recommendedCities?.length && memoizedData.city?.country?.name && (
            <Suspense fallback={null}>
              <LazyButtonlikeLinksBlock
                slideShow={isMobile}
                titleText={<Trans>Cities in {memoizedData.city.country.name}</Trans>}
                links={citiesLinks}
              />
            </Suspense>
          )}
        </LazyHydrate>

        <LazyHydrate whenVisible>
          <LinkingsGroup
            onlyPopularBlock
            linkings={linkings}
            city={memoizedData.city}
            lang={lang}
          />
        </LazyHydrate>

        <LazyHydrate whenVisible>
          <SeoSection seoHtml={seoHtml} />
        </LazyHydrate>

        <StackedAccordions data={memoizedData.attraction?.abouts} TitleTag="h2">
          <Trans>About {memoizedData.attraction?.name}</Trans>
        </StackedAccordions>

        <StackedAccordions data={memoizedData.attraction?.faqs}>
          <Trans>FAQ about {memoizedData.attraction?.name}</Trans>
        </StackedAccordions>

        <Suspense fallback={null}>
          <LazySitemapBlock city={memoizedData.city} lang={lang} cityName={cityName} />
        </Suspense>

        <JsonLd item={jsonLdData} />
        {faqJsonLdData && <JsonLd item={faqJsonLdData} />}
      </div>
    </Root>
  );
};

Attraction.getInitialProps = withRedirectToKnownLang(async ({ req, match, store }) => {
  try {
    const cookies = get(req, "headers.cookie");
    const mobile = isMobileUserAgent(getUserAgent(req));
    store.dispatch({ type: types.FETCH_USER, cookies });

    const currency = await fetchDefaultCurrency(req);
    store.dispatch({ type: types.SET_DEFAULT_CURRENCY, defaultCurrency: currency });

    const { attractionId } = match.params;
    const lang = getCurrentLanguage(match.params.lang);
    let { cityId } = match.params;
    let seoHtml = "";

    const productsPerPage = mobile ? 4 : 20;

    store.dispatch({
      type: types.FETCH_PRODUCTS,
      currency,
      lang,
      perPage: productsPerPage,
      cityId,
      attractionId,
      separateTo: "mainProducts",
    });

    store.dispatch({ type: types.FETCH_LANGUAGES });

    const overview = await fetchOverview({ lang }, req);
    store.dispatch({ type: types.SET_OVERVIEW, data: overview });

    const { data: attraction } = await Api.get(
      `/api/v2/attractions/${attractionId}/?${toQueryString({
        lang,
        expand: "meta_title,meta_description,schedule,abouts,faqs",
        preorder: true,
      })}`,
      { lang },
    );

    // Redirect from the long attraction route to the short one
    if (cityId) {
      return {
        redirectTo: reverseUrl("attraction-simple", {
          attractionId,
          attractionSlug: attraction.slug,
          lang,
        }),
      };
    }

    if (!cityId && attraction.city) {
      cityId = attraction.city.id;
    }

    seoHtml = attraction.description;

    const city = (
      await Api.get(
        `/api/v2/cities/${cityId}/?${toQueryString({
          lang,
          expand: "h1,meta_title,meta_description,description",
          preorder: true,
        })}`,
        { lang },
      )
    ).data;

    let cityAttractionsWithoutTickets;
    if (city.country?.id) {
      cityAttractionsWithoutTickets = await Api.get(
        `/api/v3/attractions/?${toQueryString({
          lang,
          city_id: cityId,
          country_id: city.country.id,
          include_preorder: false,
          per_page: 20,
          sort_by: "popularity",
        })}&with_tickets=false`,
        { lang },
      );
    }

    store.dispatch({
      type: types.FETCH_CATEGORIES,
      payload: { cityId, lang },
    });

    store.dispatch({
      type: types.FETCH_INTERESTS,
      payload: { cityId, lang },
    });

    store.dispatch({
      type: types.FETCH_MOST_RECOMMENDED,
      payload: { city: cityId, attraction: attractionId, lang },
    });

    const {
      reviews: { count: reviewsCount },
    } = store.getState();
    if (!reviewsCount) {
      store.dispatch({
        type: types.FETCH_REVIEWS,
        lang,
        attractionId,
        per_page: mobile ? 3 : 6,
      });
    }

    store.dispatch({
      type: types.FETCH_OVERALL_RATING,
      payload: { id: attractionId, type: "attraction", lang },
    });

    store.dispatch({
      type: types.FETCH_CITIES,
      payload: {
        countryId: city.country.id,
        lang,
      },
    });

    const linkings = await fetchLinkings({ lang, id: cityId });

    return {
      attractionId,
      destinations: overview.topCities,
      city,
      cityAttractionsWithoutTickets,
      productsPerPage,
      currency,
      attraction,
      mobile,
      linkings,
      seoHtml: replaceImgTagsInHtml(seoHtml),
      path: req?.originalUrl,
    };
  } catch (error) {
    // TODO: serve 500 for not 404 errors
    return { statusCode: 404 };
  }
});

const mapStateToProps = ({ products, user, cities, reviews, filters }) => ({
  products,
  cityProducts: products.cityProducts,
  userId: user.user.id,
  mostRecommended: products.mostRecommendedProducts,
  recommendedCities: cities.cities,
  categories: cities.categories,
  interests: cities.interests,
  reviews,
  overallCount: reviews.overallCount,
  filters,
});

export default connect(mapStateToProps)(Attraction);
